/**
 * 가상 키오스크 - 킨더랩스 API 시뮬레이션
 * welcome → purchase 순서로 호출하여 실제 키오스크 동작을 재현
 *
 * 2026-04-14 부터 킨더랩스가 device_type/device_id/device_name 을 전송함.
 * REAL_DEVICES, REAL_PRODUCTS 는 실제 prod api_logs(2026-04-14~) 분석 결과.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAccessToken } from '../contexts/AuthContext';
import { getTestPassportPreview, resetTestPassport } from '../services/api';
import StoreBadge from '../components/StoreBadge';
import PhoneKeypadModal from '../components/PhoneKeypadModal';
import './VirtualKioskPage.css';

const formatPhoneDisplay = (p) => {
  if (!p) return '';
  if (p.length === 11) return `${p.slice(0, 3)}-${p.slice(3, 7)}-${p.slice(7)}`;
  return p;
};

const API_BASE = process.env.REACT_APP_API_URL || 'http://192.168.0.113:59000/api/v1';

// 지점별 실제 운영 장치 번호 (prod api_logs 기준)
const REAL_DEVICES = {
  '1': { POS: [1, 15, 18, 23, 25], KIOSK: [2, 19, 20, 54, 56] },
  '2': { POS: [7, 8],              KIOSK: [] },
  '3': { POS: [11, 30],            KIOSK: [31, 32, 34] },
  '4': { POS: [39, 40, 42],        KIOSK: [45, 46, 47] },
};

// 지점별 실제 상품 샘플 (prod api_logs 2026-04-14~ 분석)
// 여권 product_id/가격: design 2026-06-13 §2.3 동결 reference (과거 분석, prod_daily 재집계 아님).
//   영등포 8038 / 동탄 8039 / 하남 8037 — 전 지점 [MD] 타입 10,000원. 경주는 여권 미판매(passport=null).
const REAL_PRODUCTS = {
  '1': {
    ticket:   { product_id: 7353, name: '[T] 1회 입장권', price: 29000 },
    passport: { product_id: 8038, name: '영등포 주렁여권 [MD]', price: 10000 },
  },
  '2': {
    // 경주는 여권 미판매(design §2.3 동결 reference) → 여권 시나리오 버튼 비활성(D2).
    ticket:   { product_id: 7072, name: '경주 단체 소인', price: 12000 },
    passport: null,
  },
  '3': {
    ticket:   { product_id: 1001, name: '주렁주렁 입장권(대인)', price: 25000 },
    passport: { product_id: 8039, name: '동탄 주렁여권 [MD]', price: 10000 },
  },
  '4': {
    // 하남은 '주렁여권 [MD]' 가 입장권 역할까지 겸함 (prod 관찰)
    ticket:   { product_id: 7603, name: '포유류/조류 10종 세트[하남]', price: 10000 },
    passport: { product_id: 8037, name: '하남 주렁여권 [MD]', price: 10000 },
  },
};

const FALLBACK_TICKET   = { product_id: 1001, name: '주렁주렁 입장권(대인)', price: 25000 };
const FALLBACK_PASSPORT = { product_id: 2001, name: '주렁여권',               price: 50000 };

// 동탄 테스트 서버 셀프 회원가입 URL (QR 대상)
const DONGTAN_REGISTER_URL = 'http://nexusnas.iptime.org:59017/?store=3';

// 순번 자동 생성
let orderSeq = Math.floor(Math.random() * 900000) + 100000;
const nextOrderId = (storeId) => {
  orderSeq++;
  const now = new Date();
  const dateStr = `${String(now.getFullYear()).slice(2)}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}`;
  return `O${dateStr}-${String(storeId).padStart(3,'0')}-${String(orderSeq).padStart(6,'0')}`;
};

const nextTicketId = () => `T${Date.now()}-${Math.floor(Math.random()*1000)}`;

const VirtualKioskPage = () => {
  const navigate = useNavigate();
  const storeId = localStorage.getItem('store_id') || '3';
  const storeName = localStorage.getItem('store_name') || '';

  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [deletePhone, setDeletePhone] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [keypadTarget, setKeypadTarget] = useState(null); // 'phone' | 'deletePhone' | null
  const [showRegisterQR, setShowRegisterQR] = useState(false);
  const [searching, setSearching] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [passportDecision, setPassportDecision] = useState(null); // { phone, memberCount, passportCount } | null — 기존 여권 보유 시 팝업
  // 쿠폰 시뮬(킨더랩스 KIOSK) — 쿠폰QR + mock 장바구니 → 적용/완료/취소
  const [cpnNo, setCpnNo] = useState('');
  const [cpnPid, setCpnPid] = useState('');
  const [cpnPrice, setCpnPrice] = useState('');
  const [cpnQty, setCpnQty] = useState('1');
  // v0.6 — 2번째 라인(1+1 에서 "서로 다른 두 상품" 재현용). 비우면 1줄만 보낸다.
  const [cpnPid2, setCpnPid2] = useState('');
  const [cpnPrice2, setCpnPrice2] = useState('');
  const [cpnQty2, setCpnQty2] = useState('');
  // v0.6 — 고객이 고른 상품ID(쉼표 구분). 비우면 필드를 아예 보내지 않아 구 경로가 된다.
  const [cpnSelected, setCpnSelected] = useState('');
  const [cpnOrderNo, setCpnOrderNo] = useState('');
  const [cpnBusy, setCpnBusy] = useState(false);

  // 장치 고정: KIOSK 31
  const DEVICE_TYPE = 'KIOSK';
  const DEVICE_ID = '31';
  const deviceName = `${DEVICE_TYPE} ${DEVICE_ID}`;

  const deviceFields = () => ({
    device_type: DEVICE_TYPE,
    device_id: DEVICE_ID,
    device_name: deviceName,
  });

  const products = REAL_PRODUCTS[storeId] || { ticket: FALLBACK_TICKET, passport: FALLBACK_PASSPORT };
  // 여권 미판매 지점(경주, store '2')은 passport=null → 여권 시나리오 비활성(D2).
  const passportProduct = products.passport;
  const passportSold = !!passportProduct;

  const addLog = (type, msg, detail = null) => {
    setLogs(prev => [{
      time: new Date().toLocaleTimeString(),
      type,
      msg,
      detail: detail ? JSON.stringify(detail, null, 2) : null
    }, ...prev].slice(0, 20));
  };

  const callAPI = async (method, path, data) => {
    const token = getAccessToken();
    const res = await axios({
      method,
      url: `${API_BASE}${path}`,
      data,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      withCredentials: true,
      timeout: 15000,
    });
    return res.data;
  };

  // 시나리오: 입장권만 구매
  const handleTicketOnly = async () => {
    setLoading(true);
    const orderId = nextOrderId(storeId);
    const ticketId = nextTicketId();
    const hasPhone = phone.length === 11;
    const device = deviceFields();

    try {
      addLog('→', `[welcome] order=${orderId} device=${deviceName}`);
      const welcomeRes = await callAPI('POST', '/tools/virtual-kiosk/welcome', {
        store_id: storeId,
        order_id: orderId,
        ...device,
        ticket_id_list: [{ ticket_id: ticketId }]
      });
      addLog('✅', `[welcome] ${welcomeRes.message || 'OK'}`, welcomeRes);

      addLog('→', `[purchase] order=${orderId}, phone=${hasPhone ? phone : '없음(비회원)'}`);
      const purchaseRes = await callAPI('POST', '/tools/virtual-kiosk/purchase', {
        telephone: hasPhone ? phone : null,
        order_id: orderId,
        store_id: storeId,
        ...device,
        items: [{ ...products.ticket, quantity: 1 }]
      });
      addLog('✅', `[purchase] ${purchaseRes.message || 'OK'}`, purchaseRes);
    } catch (err) {
      addLog('❌', `에러: ${err.response?.data?.message || err.message}`, err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // 시나리오: 입장권 + 여권 (한 주문)
  const handleTicketAndPassport = async () => {
    if (!passportProduct) {
      addLog('⚠️', `${storeName || `store ${storeId}`}: 여권 미판매 지점입니다`);
      return;
    }
    setLoading(true);
    const orderId = nextOrderId(storeId);
    const ticketId = nextTicketId();
    const hasPhone = phone.length === 11;
    const device = deviceFields();

    try {
      addLog('→', `[welcome] order=${orderId} device=${deviceName}`);
      const welcomeRes = await callAPI('POST', '/tools/virtual-kiosk/welcome', {
        store_id: storeId,
        order_id: orderId,
        ...device,
        ticket_id_list: [{ ticket_id: ticketId }]
      });
      addLog('✅', `[welcome] ${welcomeRes.message || 'OK'}`, welcomeRes);

      addLog('→', `[purchase] order=${orderId}, phone=${hasPhone ? phone : '없음(비회원)'}, 입장권+${products.passport.name}`);
      const purchaseRes = await callAPI('POST', '/tools/virtual-kiosk/purchase', {
        telephone: hasPhone ? phone : null,
        order_id: orderId,
        store_id: storeId,
        ...device,
        items: [
          { ...products.ticket, quantity: 1 },
          { ...products.passport, quantity: 1 },
        ]
      });
      addLog('✅', `[purchase] ${purchaseRes.message || 'OK'}`, purchaseRes);
    } catch (err) {
      addLog('❌', `에러: ${err.response?.data?.message || err.message}`, err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // 시나리오: 여권구매 (welcome 없음, purchase 에 여권 item 만)
  // design §4.1 — 전화번호 있으면 회원 여권구매(회원등록+여권+쿠폰), 없으면 비회원.
  // overridePhone 이 주어지면 그 번호로(검색 흐름에서 전달), 미지정이면 입력칸 phone 사용.
  const handlePassportOnly = async (overridePhone) => {
    if (!passportProduct) {
      addLog('⚠️', `${storeName || `store ${storeId}`}: 여권 미판매 지점입니다`);
      return;
    }
    const targetPhone = overridePhone !== undefined ? overridePhone : phone;
    const hasPhone = !!targetPhone && targetPhone.length === 11;
    setLoading(true);
    const orderId = nextOrderId(storeId);
    const device = deviceFields();

    try {
      addLog('→', `[purchase·여권단독] order=${orderId}, phone=${hasPhone ? targetPhone : '없음(비회원)'}, ${passportProduct.name}`);
      const purchaseRes = await callAPI('POST', '/tools/virtual-kiosk/purchase', {
        telephone: hasPhone ? targetPhone : null,
        order_id: orderId,
        store_id: storeId,
        ...device,
        items: [{ ...passportProduct, quantity: 1 }],
      });
      addLog('✅', `[purchase·여권단독] ${purchaseRes.message || 'OK'}`, purchaseRes);
      return purchaseRes;
    } catch (err) {
      addLog('❌', `에러: ${err.response?.data?.message || err.message}`, err.response?.data);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 여권구매 버튼 = 단일 진입점 (design 2026-06-13 v1.3 통합 흐름).
  // 전화번호 있으면 preview 로 기존 여권 보유 판정 → (보유 시) 팝업, 없으면 바로 구매. 미입력 → 비회원 구매.
  const handlePassportPurchaseClick = async () => {
    if (!passportProduct) {
      addLog('⚠️', `${storeName || `store ${storeId}`}: 여권 미판매 지점입니다`);
      return;
    }
    // 전화번호 미입력(또는 11자리 미만) → 비회원 여권구매(바로)
    if (!phone || phone.length !== 11) {
      await handlePassportOnly(null);
      return;
    }
    setSearching(true);
    try {
      addLog('→', `[검색] ${formatPhoneDisplay(phone)} 여권 현황 조회`);
      // api.js 응답 인터셉터가 response.data 를 반환 → 배열 또는 {members:[...]} 래핑 모두 수용.
      const res = await getTestPassportPreview(phone);
      const members = Array.isArray(res) ? res : (res?.members || []);
      // 보유 여권 전체(status 무관, CANCELLED 포함) 기준 — 리셋 scope 정합(#1).
      const hasExistingPassport = members.some(
        (m) => Array.isArray(m.passports) && m.passports.length > 0,
      );
      const passportCount = members.reduce(
        (sum, m) => sum + (Array.isArray(m.passports) ? m.passports.length : 0),
        0,
      );
      if (hasExistingPassport) {
        // 기존 여권사용자 → 팝업으로 [모두 지우고 새 여권 / 추가 여권 / 취소] 선택
        addLog('🔎', `기존 여권사용자 (회원 ${members.length}명 · 여권 ${passportCount}건) — 처리 선택`, members);
        setPassportDecision({ phone, memberCount: members.length, passportCount });
      } else {
        // 비회원 / 여권 0건 회원 → 바로 여권구매
        addLog('🔎', members.length === 0
          ? `비회원 — 바로 여권구매 (${formatPhoneDisplay(phone)})`
          : `회원 ${members.length}명, 보유 여권 0건 — 바로 여권구매`);
        await handlePassportOnly(phone);
      }
    } catch (err) {
      addLog('❌', `검색 실패: ${err?.message || err?.response?.data?.message || '오류'}`);
    } finally {
      setSearching(false);
    }
  };

  // 팝업 [모두 지우고 새 여권]: 여권 리셋(R3) 후 동일 API 재구매.
  // success 명시 확인 후에만 재구매(throw 없음 ≠ 성공 — BE 가 200+{success:false} 가능).
  const handleResetAndNewPassport = async () => {
    const targetPhone = passportDecision?.phone;
    if (!targetPhone || targetPhone.length !== 11) return;
    setResetting(true);
    let resetOk = false;
    try {
      addLog('→', `[여권 리셋] ${formatPhoneDisplay(targetPhone)} 여권+여권쿠폰 삭제`);
      const resetRes = await resetTestPassport(targetPhone);
      if (resetRes?.success === false) {
        addLog('⚠️', `[여권 리셋] 중단 — ${resetRes?.message || '실패'}`, resetRes);
      } else {
        addLog('🗑️', `[여권 리셋] ${resetRes?.message || 'OK'}`, resetRes);
        resetOk = true;
      }
    } catch (err) {
      addLog('❌', `리셋 실패: ${err?.message || err?.response?.data?.message || '오류'}`);
    } finally {
      setResetting(false);
    }
    if (!resetOk) return;
    setPassportDecision(null);
    try {
      await handlePassportOnly(targetPhone); // 회원 유지 → 회원 여권 재구매
    } catch (_) {
      /* handlePassportOnly 내부에서 이미 addLog 처리 */
    }
  };

  // 팝업 [추가 여권 구입]: 리셋 없이 동일 API 로 한 건 더 구매(기존 여권 유지).
  const handleAdditionalPassport = async () => {
    const targetPhone = passportDecision?.phone;
    setPassportDecision(null);
    if (!targetPhone) return;
    try {
      await handlePassportOnly(targetPhone);
    } catch (_) {
      /* handlePassportOnly 내부에서 이미 addLog 처리 */
    }
  };

  // 회원 삭제 (TEST 서버 전용 API)
  const handleDelete = async () => {
    if (!deletePhone || deletePhone.length !== 11) return;
    setDeleting(true);
    try {
      const token = getAccessToken();
      const res = await axios.delete(`${API_BASE}/tools/test-member/${deletePhone}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        addLog('🗑️', `${deletePhone}: ${res.data.message}`);
      } else {
        addLog('⚠️', `${deletePhone}: ${res.data.message}`);
      }
    } catch (err) {
      addLog('❌', `삭제 실패: ${err.response?.data?.message || err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // ── 쿠폰 시뮬 핸들러 (킨더랩스 KIOSK API 1/2/취소) ──
  // v0.6 — **다중 라인**. 킨더랩스 규약상 items 는 장바구니 전체이며, 1+1 의 "서로 다른
  // 두 상품" 을 재현하려면 2줄이 필요하다. 2번째 상품ID 가 비면 1줄만 만든다.
  const cpnLine = (pid, price, qty) => ({
    상품ID: Number(pid),
    상품명: `상품 ${pid}`,
    가격: Number(price) || 0,
    수량: Number(qty) || 1,
  });

  const cpnCart = () => {
    const lines = [];
    if (cpnPid) lines.push(cpnLine(cpnPid, cpnPrice, cpnQty));
    if (cpnPid2) lines.push(cpnLine(cpnPid2, cpnPrice2, cpnQty2));
    return lines;
  };

  // "1398, 1398" → [1398, 1398]. 비었거나 전부 무효면 null → 필드 미전송(구 경로).
  const cpnSelectedIds = () => {
    const ids = cpnSelected
      .split(',')
      .map((t) => Number(t.trim()))
      .filter((v) => Number.isInteger(v) && v > 0);
    return ids.length ? ids : null;
  };

  const handleCouponInfo = async () => {
    if (!cpnNo) { addLog('⚠️', '쿠폰번호를 입력하세요'); return; }
    setCpnBusy(true);
    try {
      addLog('→', `[쿠폰 조회] coupon=${cpnNo}`);
      const res = await callAPI('POST', '/tools/virtual-kiosk/coupon/info', {
        store_id: String(storeId), coupon_no: cpnNo,
      });
      if (res.valid === false) {
        addLog('🚫', `무효: ${res.reason_code} — ${res.message || ''}`, res);
      } else {
        addLog('✅', `[조회] ${res.coupon_type} · 골라야 할 개수 ${res.select_count} · 선택가능 ${JSON.stringify(res.selectable_products || [])}`, res);
      }
    } catch (err) { addLog('❌', `조회 에러: ${err.response?.data?.message || err.message}`, err.response?.data); }
    finally { setCpnBusy(false); }
  };

  const handleCouponApply = async () => {
    if (!cpnNo || !cpnPid) { addLog('⚠️', '쿠폰번호와 상품ID를 입력하세요'); return; }
    setCpnBusy(true);
    try {
      const selected = cpnSelectedIds();
      const cart = cpnCart();
      addLog('→', `[쿠폰 적용] coupon=${cpnNo} cart=${cart.length}줄${selected ? ` 선택=${JSON.stringify(selected)}` : ' (선택 미전송 — 구 경로)'}`);
      const body = { store_id: String(storeId), coupon_no: cpnNo, items: cart };
      // 🔴 비었으면 키를 넣지 않는다 — 구 키오스크 요청과 같은 형태여야 로그 대조가 된다.
      if (selected) body.selected_product_ids = selected;
      const res = await callAPI('POST', '/tools/virtual-kiosk/coupon/apply', body);
      if (res.applicable === false) addLog('🚫', `적용 불가: ${res.reason_code} — ${res.message || ''}`, res);
      else addLog('✅', `[쿠폰 적용] 효과 반영 상품목록 ${res.items?.length || 0}건 (할인합계 ${res.discount_total ?? 0})`, res);
    } catch (err) { addLog('❌', `적용 에러: ${err.response?.data?.message || err.message}`, err.response?.data); }
    finally { setCpnBusy(false); }
  };

  const handleCouponComplete = async () => {
    if (!cpnNo) { addLog('⚠️', '쿠폰번호를 입력하세요'); return; }
    const orderNo = cpnOrderNo || `KIOSK-SIM-${Date.now()}`;
    setCpnOrderNo(orderNo);
    setCpnBusy(true);
    try {
      addLog('→', `[결제완료] coupon=${cpnNo} order=${orderNo}`);
      const res = await callAPI('POST', '/tools/virtual-kiosk/coupon/complete', {
        store_id: String(storeId), coupon_no: cpnNo, order_id: orderNo,
      });
      addLog(res.idempotent_hit ? '♻️' : '✅', `[결제완료] redemption=${res.redemption_id} ${res.idempotent_hit ? '(멱등 hit)' : ''}`, res);
    } catch (err) { addLog('❌', `완료 에러: ${err.response?.data?.message || err.message}`, err.response?.data); }
    finally { setCpnBusy(false); }
  };

  const handleCouponCancel = async () => {
    if (!cpnOrderNo) { addLog('⚠️', '먼저 결제완료(주문번호 생성) 후 취소하세요'); return; }
    setCpnBusy(true);
    try {
      addLog('→', `[사용취소] order=${cpnOrderNo}`);
      const res = await callAPI('POST', '/tools/virtual-kiosk/coupon/cancel', {
        order_id: cpnOrderNo, reason: '가상키오스크 시뮬 취소',
      });
      addLog('🔄', `[사용취소] refund=${res.refund_id} 복구=${String(res.coupon_restored)}`
        + (res.recall_reason ? ` 회수=${res.recall_reason}` : ''), res);
    } catch (err) { addLog('❌', `취소 에러: ${err.response?.data?.message || err.message}`, err.response?.data); }
    finally { setCpnBusy(false); }
  };

  return (
    <div className="vk-page">
      <header className="vk-header">
        <button className="btn-back" onClick={() => navigate('/')}>← 메뉴</button>
        <div className="vk-header-center">
          <h1>🖥️ 가상 키오스크</h1>
          <StoreBadge />
        </div>
        <div className="vk-header-spacer" />
      </header>

      <main className="vk-content">
        <div className="vk-left">
          {/* 전화번호 */}
          <div className="vk-section">
            <label className="vk-label">전화번호 (선택)</label>
            <button
              type="button"
              className={`vk-input vk-input-btn ${!phone ? 'is-placeholder' : ''}`}
              onClick={() => setKeypadTarget('phone')}
            >
              {phone ? formatPhoneDisplay(phone) : '탭하여 입력 (빈칸=비회원)'}
            </button>
          </div>

          {/* 시나리오 버튼 */}
          <div className="vk-section">
            <label className="vk-label">시나리오 선택</label>
            <div className="vk-buttons">
              <button className="vk-btn vk-btn-ticket" onClick={handleTicketOnly} disabled={loading}>
                🎫 입장권만 ({products.ticket.name})
              </button>
              <button
                className="vk-btn vk-btn-passport"
                onClick={handleTicketAndPassport}
                disabled={loading || !passportSold}
                title={passportSold ? '' : '여권 미판매 지점'}
              >
                🎫+🛂 입장권+여권
              </button>
              <button
                className="vk-btn vk-btn-passport-only"
                onClick={handlePassportPurchaseClick}
                disabled={loading || searching || resetting || !passportSold}
                title={passportSold ? '' : '여권 미판매 지점'}
              >
                {searching
                  ? '🔎 여권 확인 중...'
                  : `🛂 여권구매 ${passportProduct ? `(${passportProduct.name})` : '(미판매)'}`}
              </button>
            </div>
            {!passportSold && (
              <div className="vk-loading">이 지점은 여권을 판매하지 않습니다 (여권 시나리오 비활성)</div>
            )}
            {loading && <div className="vk-loading">처리 중...</div>}
          </div>

          {/* 동탄 회원가입 QR */}
          <div className="vk-section">
            <label className="vk-label">📱 동탄 회원가입 QR (TEST)</label>
            <button
              className={`vk-btn vk-btn-qr ${showRegisterQR ? 'is-active' : ''}`}
              onClick={() => setShowRegisterQR(v => !v)}
            >
              {showRegisterQR ? '✕ QR 닫기' : '📱 QR 보여주기'}
            </button>
          </div>

          {/* 회원 삭제 */}
          <div className="vk-section vk-delete-section">
            <label className="vk-label">🗑️ 테스트 회원 삭제</label>
            <div className="vk-delete-row">
              <button
                type="button"
                className={`vk-input vk-input-btn ${!deletePhone ? 'is-placeholder' : ''}`}
                onClick={() => setKeypadTarget('deletePhone')}
              >
                {deletePhone ? formatPhoneDisplay(deletePhone) : '탭하여 전화번호 입력'}
              </button>
              <button className="vk-btn vk-btn-delete" onClick={handleDelete} disabled={deleting || deletePhone.length !== 11}>
                {deleting ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>

          {/* 쿠폰 시뮬 (킨더랩스 KIOSK) */}
          <div className="vk-section">
            <label className="vk-label">🎟️ 쿠폰 시뮬 (KIOSK 적용→완료→취소)</label>
            <input
              className="vk-input"
              placeholder="쿠폰번호 (CPN_… 또는 EAN-13 13자리)"
              value={cpnNo}
              onChange={(e) => setCpnNo(e.target.value.trim())}
            />
            <div className="vk-delete-row" style={{ marginTop: 8 }}>
              <input className="vk-input" placeholder="상품ID" value={cpnPid}
                onChange={(e) => setCpnPid(e.target.value.replace(/[^0-9]/g, ''))} />
              <input className="vk-input" placeholder="가격" value={cpnPrice}
                onChange={(e) => setCpnPrice(e.target.value.replace(/[^0-9]/g, ''))} />
              <input className="vk-input" placeholder="수량" value={cpnQty}
                onChange={(e) => setCpnQty(e.target.value.replace(/[^0-9]/g, ''))} />
            </div>
            <div className="vk-delete-row" style={{ marginTop: 8 }}>
              <input className="vk-input" placeholder="상품ID2 (선택)" value={cpnPid2}
                onChange={(e) => setCpnPid2(e.target.value.replace(/[^0-9]/g, ''))} />
              <input className="vk-input" placeholder="가격2" value={cpnPrice2}
                onChange={(e) => setCpnPrice2(e.target.value.replace(/[^0-9]/g, ''))} />
              <input className="vk-input" placeholder="수량2" value={cpnQty2}
                onChange={(e) => setCpnQty2(e.target.value.replace(/[^0-9]/g, ''))} />
            </div>
            <input
              className="vk-input"
              style={{ marginTop: 8 }}
              placeholder="선택한 상품ID (쉼표 구분, 예: 1398, 1398) — 비우면 구 경로"
              value={cpnSelected}
              onChange={(e) => setCpnSelected(e.target.value.replace(/[^0-9, ]/g, ''))}
            />
            <div className="vk-buttons" style={{ marginTop: 8 }}>
              <button className="vk-btn vk-btn-info" onClick={handleCouponInfo} disabled={cpnBusy}>⓪ 조회</button>
              <button className="vk-btn vk-btn-ticket" onClick={handleCouponApply} disabled={cpnBusy}>① 적용</button>
              <button className="vk-btn vk-btn-passport" onClick={handleCouponComplete} disabled={cpnBusy}>② 결제완료</button>
              <button className="vk-btn vk-btn-delete" onClick={handleCouponCancel} disabled={cpnBusy || !cpnOrderNo}>취소</button>
            </div>
            {cpnOrderNo && <div className="vk-loading">주문번호: {cpnOrderNo}</div>}
          </div>
        </div>

        {/* 우: QR 또는 로그 */}
        <div className="vk-right">
          {showRegisterQR ? (
            <div className="vk-qr-panel">
              <div className="vk-qr-title">📱 동탄 회원가입 QR</div>
              <div className="vk-qr-sub">휴대폰으로 스캔하여 셀프 회원가입</div>
              <img
                className="vk-qr-image"
                alt="동탄 회원가입 QR"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=360x360&margin=10&data=${encodeURIComponent(DONGTAN_REGISTER_URL)}`}
              />
              <a className="vk-qr-url" href={DONGTAN_REGISTER_URL} target="_blank" rel="noreferrer">
                {DONGTAN_REGISTER_URL}
              </a>
            </div>
          ) : (
            <>
              <div className="vk-log-header">
                <span>📋 API 호출 로그</span>
                <button className="vk-btn-clear" onClick={() => setLogs([])}>지우기</button>
              </div>
              <div className="vk-log-list">
                {logs.length === 0 && <div className="vk-log-empty">시나리오를 선택하면 결과가 여기 표시됩니다</div>}
                {logs.map((log, i) => (
                  <div key={i} className={`vk-log-item vk-log-${log.type === '✅' ? 'ok' : log.type === '❌' ? 'err' : log.type === '🗑️' ? 'del' : 'info'}`}>
                    <div className="vk-log-head">
                      <span className="vk-log-type">{log.type}</span>
                      <span className="vk-log-msg">{log.msg}</span>
                      <span className="vk-log-time">{log.time}</span>
                    </div>
                    {log.detail && (
                      <pre className="vk-log-detail">{log.detail}</pre>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {passportDecision && (
        <div className="vk-modal-overlay" onClick={() => !resetting && setPassportDecision(null)}>
          <div className="vk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vk-modal-title">이미 여권이 있는 회원입니다</div>
            <div className="vk-modal-sub">
              {formatPhoneDisplay(passportDecision.phone)}
              <br />
              회원 {passportDecision.memberCount}명 · 보유 여권 {passportDecision.passportCount}건
            </div>
            <div className="vk-modal-q">어떻게 진행할까요?</div>
            <div className="vk-modal-actions">
              <button
                className="vk-btn vk-btn-reset"
                onClick={handleResetAndNewPassport}
                disabled={resetting || loading}
              >
                {resetting ? '리셋 중...' : '♻️ 모두 지우고 새 여권 구매'}
              </button>
              <button
                className="vk-btn vk-btn-passport-only"
                onClick={handleAdditionalPassport}
                disabled={resetting || loading}
              >
                ➕ 추가 여권 구입 (기존 유지)
              </button>
              <button
                className="vk-btn vk-btn-cancel"
                onClick={() => setPassportDecision(null)}
                disabled={resetting || loading}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      <PhoneKeypadModal
        open={keypadTarget !== null}
        title={keypadTarget === 'deletePhone' ? '삭제할 전화번호' : '회원 전화번호'}
        initialPhone={keypadTarget === 'deletePhone' ? deletePhone : phone}
        allowEmpty={keypadTarget === 'phone'}
        onClose={() => setKeypadTarget(null)}
        onConfirm={(fullPhone) => {
          if (keypadTarget === 'deletePhone') setDeletePhone(fullPhone);
          else if (keypadTarget === 'phone') setPhone(fullPhone);
        }}
      />
    </div>
  );
};

export default VirtualKioskPage;
